import * as React from 'react';
import * as ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import withTheme from '../utils/withTheme';
import { useEffect } from '../utils/helper';
import { useForkRef, setRef } from '../utils/ref';

function getContainer(container) {
	container = typeof container === 'function' ? container() : container;
	// #StrictMode ready
	return ReactDOM.findDOMNode(container) || document.body;
}

const Portal = withTheme(
	React.forwardRef(function Portal(props, ref) {
		const { children, container, disablePortal } = props;
		const [mountNode, setMountNode] = React.useState(null);
		const handleRef = useForkRef(
			React.isValidElement(children) ? children.ref : null,
			ref
		);
		useEffect(() => {
			if (!disablePortal) {
				setMountNode(getContainer(container));
			}
		}, [container, disablePortal]);

		useEffect(() => {
			if (mountNode && !disablePortal) {
				setRef(ref, mountNode);
				return () => {
					setRef(ref, null);
				};
			}
			return undefined;
		}, [ref, mountNode, disablePortal]);

		if (disablePortal) {
			if (React.isValidElement(children)) {
				return React.cloneElement(children, {
					ref: handleRef,
				});
			}
			return children;
		}

		return mountNode ? ReactDOM.createPortal(children, mountNode) : mountNode;
	})
);

Portal.propTypes = {
	/**
	 * The children to render into the `container`.
	 */
	children: PropTypes.node,
	/**
	 * Disable the Portal. The children stay within it's parent DOM hierarchy.
	 */
	disablePortal: PropTypes.bool,
	/**
	 * A node, component instance, or function that returns either.
	 * The `container` will have the portal children appended to it.
	 * By default, it uses the body of the top-level document object,
	 * so it's simply `document.body` most of the time.
	 */
	container: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.instanceOf(React.Component),
		PropTypes.instanceOf(typeof Element === 'undefined' ? Object : Element),
	]),
};

Portal.defaultProps = {
	disablePortal: false,
};

Portal.displayName = 'NbPortal';

export default Portal;
